import React, { useEffect, useRef, useState } from "react";
import { DeviceEventEmitter, Platform, StyleSheet, View } from "react-native";
import Toast from "react-native-toast-message";

import { useFocusEffect, CommonActions } from "@react-navigation/native";
import Container from "@/components/shared/AppContainer";
import { useAuth } from "@/components/AuthContext";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import { useLayoutMode } from "@/components/layout/layoutMode";
import {
  fetchEvents,
  getEventByAlias,
  subscribeToEventsUpdates,
} from "@/components/http/events";
import {
  clearPendingOnboardingEventNavigation,
  readPendingOnboardingEventNavigation,
} from "@/components/pendingOnboardingEventNavigation";
import AppActivityIndicator from "@/components/shared/AppActivityIndicator";
import AppEventGrid from "@/components/shared/AppEventGrid";
import AppSubtitle from "@/components/shared/AppSubtitle";
import AppTitle from "@/components/shared/AppTitle";
import DashboardHeader from "@/components/shared/DashboardHeader";
import NewTestButton from "@/components/shared/NewTestButton";
import useNow from "@/components/hooks/useNow";
import AppInput from "@/components/shared/AppInput";
import OnboardingButton from "@/components/shared/OnboardingButton";
import tw from "twrnc";
import { DONATIONS_ENABLED } from "@/components/donations/config";
import {
  clearFundraiserIntent,
  nonprofitLeadOf,
  readFundraiserIntent,
  updateFundraiserIntent,
} from "@/components/donations/fundraiserIntent";
import FundraiserSetupBanner from "@/components/donations/FundraiserSetupBanner";
import { waitForNonprofitWaiverClaim } from "@/components/donations/nonprofitWaiver";
import FuneralWelcomeBanner from "@/components/funeral/FuneralWelcomeBanner";
import {
  clearFuneralHomeIntent,
  readFuneralHomeIntent,
} from "@/components/funeral/funeralHomeIntent";
import {
  canAddRoom,
  groupEventsWithRooms,
  matchesSearch,
} from "@/components/rooms/rooms";
import useAddRoom from "@/components/rooms/useAddRoom";
import { rememberRoomCopy } from "@/components/rooms/roomCopies";
import { ROOMS_CHANGED } from "@/components/rooms/useRoomGroup";
import { t } from "@/localization";
import { useTeamAccess } from "@/components/teams/useWorkspace";
import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { fetchSharedEvents } from "@/components/teams/api";
import { refreshSharedAccess } from "@/components/teams/actions";
import { teamsErrorMessage } from "@/components/teams/errors";
import { openWorkspace } from "@/components/teams/openWorkspace";
import { readPendingInvite } from "@/components/teams/pendingInvite";
import SharedWithYou from "@/components/teams/SharedWithYou";
import WorkspaceBanner from "@/components/teams/WorkspaceBanner";
import Callout from "@/components/settings/Callout";
import Button, { ButtonRow } from "@/components/settings/Button";

// On big monitors the dashboard keeps its header buttons and cards together.
const styles = StyleSheet.create({
  wideContent: { width: "100%", maxWidth: 1440, alignSelf: "center" },
});

const PENDING_ONBOARDING_EVENT_RETRY_MS = 1500;
const PENDING_ONBOARDING_EVENT_MAX_WAIT_MS = 120000;

const EventList = ({ navigation, route }) => {
  const [loading, setloading] = useState(true);
  const [events, setevents] = useState([]);
  const [search, setsearch] = useState("");
  const [fundraiserIntent, setFundraiserIntent] = useState(null);
  const [funeralIntent, setFuneralIntent] = useState(null);
  // Teams: why the shared account's events didn't load.
  const [listError, setListError] = useState(null);

  const now = useNow();
  const auth = useAuth();
  const {
    broadcaster,
    prefer_multiple_tests,
    profileLoaded,
    account_type,
    broadcaster_role,
    credits,
    refetchProfile,
  } = auth;
  // A group's first room opens Add a room (the explainer) for that event.
  const { openAddRoom } = useAddRoom({
    navigation,
    showIntro: (primary) => {
      rememberRoomCopy(primary);
      navigation.navigate("AddRoom", { slug: primary.slug });
    },
  });

  // Teams: the account this list shows (yours, or one shared with you). With
  // only some of its events shared, the list is those events.
  const team = useTeamAccess();
  const workspaceAuth = useWorkspaceAuth();
  const listOwner = team.isShared ? team.owner : broadcaster;
  const sharedSlugs =
    team.isShared && team.access && !team.access.accountRole
      ? team.access.slugs
      : null;
  const sharedSlugsKey = sharedSlugs ? sharedSlugs.join(",") : "";
  const listKey = `${listOwner || ""}|${sharedSlugsKey}`;
  const isFuneralHome = workspaceAuth.broadcaster_role === "funeralhome";

  const isMobileUX = useIsMobileUX();
  const wideLayout = useLayoutMode() !== "phone";

  const sortEvents = (arr) =>
    [...arr].sort((a, b) =>
      a.event_status === "live"
        ? -1
        : b.event_datetime_utc > a.event_datetime_utc
        ? 1
        : -1
    );

  const fetch = async () => {
    try {
      setloading(true);
      setListError(null);
      const eventsUnsorted = sharedSlugs
        ? await fetchSharedEvents(sharedSlugs)
        : await fetchEvents({
            onPage: (accumulated) => {
              setevents(sortEvents(accumulated));
            },
          });

      setevents(sortEvents(eventsUnsorted));
      return eventsUnsorted;
    } catch (err) {
      if (team.isShared) {
        setListError(
          teamsErrorMessage(
            err,
            t("Couldn't load {team}'s events. Try again.", { team: team.name })
          )
        );
        return [];
      }
      Toast.show({
        text1: String(err),
        type: "error",
        visibilityTime: 5000,
      });
      return [];
    } finally {
      setloading(false);
    }
  };

  // Another account: nothing of the previous one stays on screen.
  const shownListKey = useRef(listKey);
  useEffect(() => {
    if (shownListKey.current === listKey) return;
    shownListKey.current = listKey;
    setevents([]);
    setsearch("");
    setListError(null);
  }, [listKey]);

  // Keep a ref to current events so pending-nav resolution can read the
  // latest list without re-triggering the focus effect on every update.
  const eventsRef = useRef(events);
  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  // New accounts can finish onboarding before their server-created event is
  // queryable. Retry for a short window, then open the same stack used by the
  // in-app create-event flow once the event becomes available.
  useFocusEffect(
    React.useCallback(() => {
      let cancelled = false;
      let retryTimeout;

      // A nonprofit arriving from eventlive.pro/nonprofit (signup or already
      // signed in) is taken once to the fundraiser create flow; afterwards a
      // banner on this list keeps the setup one tap away.
      const processFundraiserIntent = async () => {
        let intent = await readFundraiserIntent();
        if (cancelled) return;
        // Nonprofits claim the free-streaming waiver before their first event
        // is created (events are unlocked at creation). Any build, signed in:
        // this effect runs again once the profile has loaded.
        let refreshProfile = false;
        if (profileLoaded && nonprofitLeadOf(intent)) {
          const claim = await waitForNonprofitWaiverClaim();
          if (cancelled) return;
          // The account became business/nonprofit on evt.live.
          refreshProfile =
            claim?.status === "granted" && account_type !== "business";
          intent = await readFundraiserIntent();
          if (cancelled) return;
        }
        const guided =
          DONATIONS_ENABLED &&
          !!intent?.autoStart &&
          !intent.prompted &&
          intent.stage === "captured";
        // Refreshing changes account_type and re-runs this effect, so leave it
        // to CreateEvent when we are about to go there.
        if (refreshProfile && !guided) refetchProfile();
        if (!DONATIONS_ENABLED) return;
        setFundraiserIntent(intent);
        if (
          !intent ||
          !intent.autoStart ||
          intent.prompted ||
          intent.stage !== "captured"
        ) {
          return;
        }
        if (!profileLoaded) {
          retryTimeout = setTimeout(
            processPendingNavigation,
            PENDING_ONBOARDING_EVENT_RETRY_MS
          );
          return;
        }
        const prompted = await updateFundraiserIntent({ prompted: true });
        if (cancelled) return;
        setFundraiserIntent(prompted);
        navigation.navigate("CreateEvent", { fundraiser: true });
      };

      const processPendingNavigation = async () => {
        // About your own account: not while working in someone else's.
        if (team.isShared) return;
        const pending = await readPendingOnboardingEventNavigation();
        if (cancelled) return;
        if (!pending) {
          await processFundraiserIntent();
          return;
        }

        if (
          Date.now() - pending.createdAt >
          PENDING_ONBOARDING_EVENT_MAX_WAIT_MS
        ) {
          await clearPendingOnboardingEventNavigation();
          return;
        }

        if (!profileLoaded) {
          retryTimeout = setTimeout(
            processPendingNavigation,
            PENDING_ONBOARDING_EVENT_RETRY_MS
          );
          return;
        }

        // Prefer the events list — it's the source of truth for EventList.
        // Fall back to a direct alias lookup so we don't block on the full
        // pagination and so the paywall can open the moment the backend
        // finishes creating the event.
        let resolved =
          eventsRef.current.find((e) => e.slug === pending.slug) ||
          eventsRef.current.find((e) => e.alias === pending.alias);

        if (!resolved?.slug && pending.alias) {
          try {
            const aliasEvent = await getEventByAlias(pending.alias);
            if (aliasEvent?.slug) {
              resolved = aliasEvent;
            }
          } catch (error) {
            console.warn("Failed to resolve onboarding event by alias", error);
          }
        }

        if (cancelled) return;

        if (!resolved?.slug) {
          retryTimeout = setTimeout(
            processPendingNavigation,
            PENDING_ONBOARDING_EVENT_RETRY_MS
          );
          return;
        }

        // Inject into the local list so the event appears when the user
        // returns to EventList (e.g. after closing the paywall without
        // buying) — the next focused fetch will reconcile.
        setevents((prev) =>
          prev.some((e) => e.slug === resolved.slug)
            ? prev
            : sortEvents([resolved, ...prev])
        );

        await clearPendingOnboardingEventNavigation();

        const event = resolved;
        const routes = [
          { name: "EventList" },
          { name: "EventEdit", params: { slug: event.slug, event } },
        ];

        const fundraiser = DONATIONS_ENABLED
          ? await readFundraiserIntent()
          : null;
        if (cancelled) return;

        if (fundraiser && fundraiser.stage === "captured") {
          // Onboarding created the event: continue to fundraiser setup.
          await updateFundraiserIntent({
            stage: "event_created",
            slug: event.slug,
            boundAt: Date.now(),
            prompted: true,
          });
          routes.push({
            name: "EventDonations",
            params: { slug: event.slug, event },
          });
        } else if (pending.openPaywall && !event.is_published) {
          routes.push(
            account_type === "business"
              ? {
                  name: "Plans",
                  params: {
                    source: "event_paywall",
                    event,
                    slug: event.slug,
                  },
                }
              : {
                  name: "PayWall",
                  params: {
                    event,
                    slug: event.slug,
                  },
                }
          );
        }

        navigation.dispatch(
          CommonActions.reset({
            index: routes.length - 1,
            routes,
          })
        );
      };

      processPendingNavigation();

      return () => {
        cancelled = true;
        if (retryTimeout) clearTimeout(retryTimeout);
      };
    }, [account_type, navigation, profileLoaded, team.isShared])
  );

  // Teams: a team invite opened before signing in (components/screens/TeamInvite.js),
  // and the accounts shared with you for the strip.
  useFocusEffect(
    React.useCallback(() => {
      if (!team.enabled || !profileLoaded) return;
      let active = true;
      readPendingInvite().then((pending) => {
        if (active && pending) {
          navigation.navigate("TeamInvite", { token: pending.token });
        }
      });
      refreshSharedAccess();
      return () => {
        active = false;
      };
    }, [team.enabled, profileLoaded, navigation])
  );

  useFocusEffect(
    React.useCallback(() => {
      // Teams: wait until a remembered shared account is confirmed.
      if (listOwner && team.ready) {
        console.log("callback broadcaster", listOwner);
        fetch();

        return subscribeToEventsUpdates(listOwner, (updatedEvent) => {
          if (!updatedEvent) {
            fetch();
            return;
          }
          // A new room (or any event this list hasn't loaded yet): reload.
          // With only some events shared, other events aren't for this list.
          if (
            updatedEvent.slug &&
            !eventsRef.current.some((e) => e.slug === updatedEvent.slug)
          ) {
            if (!sharedSlugs) fetch();
            return;
          }
          // Payloads carry null for fields the mutation didn't select (room
          // links included): only merge the values that came through.
          const changes = Object.fromEntries(
            Object.entries(updatedEvent).filter(([, v]) => v !== null)
          );
          setevents((prevEvents) => {
            const newEvents = prevEvents.map((event) =>
              event.slug === updatedEvent.slug
                ? { ...event, ...changes }
                : event
            );
            // Maintain the same sorting as in fetch()
            return newEvents.sort((a, b) =>
              a.event_status === "live"
                ? -1
                : b.event_datetime_utc > a.event_datetime_utc
                ? 1
                : -1
            );
          });
        });
      }
    }, [listOwner, team.ready, sharedSlugsKey])
  );

  // A funeral home from eventlive.pro/funeral-streaming is welcomed with its
  // free services until it schedules its first one or dismisses the banner.
  useFocusEffect(
    React.useCallback(() => {
      let active = true;
      readFuneralHomeIntent().then(
        (intent) => active && setFuneralIntent(intent)
      );
      return () => {
        active = false;
      };
    }, [])
  );
  const hasScheduledService = events.some((e) => !e.is_test);
  useEffect(() => {
    if (funeralIntent && hasScheduledService) {
      setFuneralIntent(null);
      clearFuneralHomeIntent();
    }
  }, [funeralIntent, hasScheduledService]);
  const funeralBannerIntent =
    !team.isShared &&
    funeralIntent &&
    !funeralIntent.dismissed &&
    !hasScheduledService &&
    !loading
      ? funeralIntent
      : null;
  const dismissFuneralWelcome = async () => {
    setFuneralIntent(null);
    await clearFuneralHomeIntent();
  };

  useEffect(() => {
    const sub = DeviceEventEmitter.addListener(ROOMS_CHANGED, () => fetch());
    return () => sub && sub.remove();
  }, []);

  useEffect(() => {
    // Can't subscribe to event delete via appsync because the mutation is missing broadcaster param
    const sub = DeviceEventEmitter.addListener("event_delete", (slug) => {
      setevents([...events.filter((e) => e.slug !== slug)]);
    });
    return () => sub && sub.remove();
  }, [events]);

  // Rooms are listed under their event, not as cards of their own.
  const filteredEvents = groupEventsWithRooms(
    (events || []).filter((e) => (prefer_multiple_tests ? true : !e.is_test))
  ).filter((e) => matchesSearch(e, search));

  const onAddRoom = (primary) =>
    openAddRoom(primary, (primary.rooms || []).length);
  // Teams: adding a room costs the account's credits (admins and up).
  const roomsAddable = (primary) =>
    team.can("addRoom", primary) &&
    canAddRoom(primary, workspaceAuth, (primary.rooms || []).length);
  const canCreate = team.can("createEvent");

  const gridRef = useRef(null);

  const fundraiserEvent =
    fundraiserIntent?.stage === "event_created" && fundraiserIntent.slug
      ? events.find((e) => e.slug === fundraiserIntent.slug)
      : null;

  // The event the intent was bound to is gone (deleted, or another account
  // signed in): offer creating a fundraiser event again instead.
  useEffect(() => {
    if (
      !DONATIONS_ENABLED ||
      loading ||
      fundraiserIntent?.stage !== "event_created" ||
      // A just-created event can take a moment to appear in the list.
      Date.now() - (fundraiserIntent.boundAt || 0) <
        PENDING_ONBOARDING_EVENT_MAX_WAIT_MS ||
      events.some((e) => e.slug === fundraiserIntent.slug)
    ) {
      return;
    }
    let active = true;
    updateFundraiserIntent({ stage: "captured", slug: "" }).then(
      (intent) => active && setFundraiserIntent(intent)
    );
    return () => {
      active = false;
    };
  }, [events, loading, fundraiserIntent]);

  const fundraiserBannerIntent =
    DONATIONS_ENABLED &&
    !team.isShared &&
    fundraiserIntent &&
    (fundraiserIntent.stage === "captured" || fundraiserEvent)
      ? fundraiserIntent
      : null;

  const openFundraiserSetup = () => {
    if (fundraiserEvent) {
      navigation.dispatch(
        CommonActions.reset({
          index: 2,
          routes: [
            { name: "EventList" },
            {
              name: "EventEdit",
              params: { slug: fundraiserEvent.slug, event: fundraiserEvent },
            },
            {
              name: "EventDonations",
              params: { slug: fundraiserEvent.slug, event: fundraiserEvent },
            },
          ],
        })
      );
      return;
    }
    navigation.navigate("CreateEvent", { fundraiser: true });
  };

  const dismissFundraiserSetup = async () => {
    setFundraiserIntent(null);
    await clearFundraiserIntent();
  };

  return (
    <>
      <Container>
        <View style={[tw`flex-1`, wideLayout && styles.wideContent]}>
          <DashboardHeader navigation={navigation} />
          {/* Teams: the web shows this banner above every screen (App.js). */}
          {Platform.OS !== "web" && (
            <WorkspaceBanner navigation={navigation} style={tw`mt-2`} />
          )}
          {team.enabled && !team.isShared && (
            <SharedWithYou
              navigation={navigation}
              style={tw`mx-${isMobileUX ? "6" : "10"} mt-3`}
            />
          )}
          {!!listError && (
            <Callout
              tone="danger"
              style={tw`mx-${isMobileUX ? "6" : "10"} mt-3`}
              action={
                <ButtonRow align="start">
                  <Button
                    text={t("Try again")}
                    size="sm"
                    fullWidth={false}
                    onPress={() => fetch()}
                  />
                  <Button
                    text={t("Switch to my account")}
                    size="sm"
                    variant="tertiary"
                    fullWidth={false}
                    onPress={() =>
                      openWorkspace(navigation, null, { source: "list_error" })
                    }
                  />
                </ButtonRow>
              }
            >
              {listError}
            </Callout>
          )}
          {!!fundraiserBannerIntent && (
            <FundraiserSetupBanner
              intent={fundraiserBannerIntent}
              event={fundraiserEvent}
              style={tw`mx-${isMobileUX ? "6" : "10"} mt-2 mb-2`}
              onContinue={openFundraiserSetup}
              onDismiss={dismissFundraiserSetup}
            />
          )}
          {!!funeralBannerIntent && (
            <FuneralWelcomeBanner
              intent={funeralBannerIntent}
              credits={credits}
              style={tw`mx-${isMobileUX ? "6" : "10"} mt-2 mb-2`}
              onDismiss={dismissFuneralWelcome}
            />
          )}
          {events?.length > 3 && (
            <View style={tw`mx-${isMobileUX ? "6" : "10"} mt-2`}>
              <AppInput
                placeholder={t("Search for an event")}
                value={search}
                onChangeText={(v) => setsearch(v)}
              />
            </View>
          )}

          {(filteredEvents.length > 0 || !!search) && (
            <AppEventGrid
              now={now}
              events={filteredEvents}
              navigation={navigation}
              gridRef={gridRef}
              onAddRoom={onAddRoom}
              roomsAddable={roomsAddable}
            />
          )}

          {filteredEvents.length === 0 &&
            !search &&
            !loading &&
            team.ready &&
            !listError &&
            team.isShared && (
              <View
                style={tw`flex-1 self-center items-center justify-center mx-5 web:mt-[10%] ios:mt-0 android:mt-0`}
              >
                <AppTitle text={t("No events yet")} />
                <AppSubtitle
                  text={t("Events in {team}'s account will show up here.", {
                    team: team.name,
                  })}
                  style={tw`text-center my-4 leading-6`}
                />
                {canCreate && (
                  <OnboardingButton
                    onPress={() => {
                      navigation.navigate(
                        "CreateEvent",
                        isFuneralHome ? { funeral: true } : undefined
                      );
                    }}
                    text={
                      isFuneralHome
                        ? t("Schedule a Service")
                        : t("Create an Event")
                    }
                    loading={false}
                    style={tw`w-full`}
                  />
                )}
              </View>
            )}

          {filteredEvents.length === 0 &&
            !search &&
            !loading &&
            !team.isShared && (
              <View
                style={tw`flex-1 self-center items-center justify-center mx-5 web:mt-[10%] ios:mt-0 android:mt-0`}
              >
                {filteredEvents.length === 0 && (
                  <>
                    <AppTitle
                      onPress={() => {
                        navigation.navigate(
                          "CreateEvent",
                          isFuneralHome ? { funeral: true } : undefined
                        );
                      }}
                      text={
                        isFuneralHome
                          ? t("Schedule your first service")
                          : t("Create your first event")
                      }
                    />
                    <AppSubtitle
                      text={
                        isFuneralHome
                          ? t(
                              "Each service gets a private link to share with the family. Families watch on any device, with no app or login."
                            )
                          : t(
                              "It looks like you have no events at the moment. Create one to see it here"
                            )
                      }
                      style={tw`text-center my-4 leading-6`}
                      onPress={() => {
                        navigation.navigate(
                          "CreateEvent",
                          isFuneralHome ? { funeral: true } : undefined
                        );
                      }}
                    />
                    <OnboardingButton
                      onPress={() => {
                        navigation.navigate(
                          "CreateEvent",
                          isFuneralHome ? { funeral: true } : undefined
                        );
                      }}
                      text={
                        isFuneralHome
                          ? t("Schedule a Service")
                          : t("Create an Event")
                      }
                      loading={false}
                      style={tw`w-full`}
                    />
                  </>
                )}
              </View>
            )}
          {(loading || !team.ready) && filteredEvents.length === 0 && (
            <View style={tw`flex-1 justify-center items-center`}>
              <AppActivityIndicator />
            </View>
          )}
        </View>

        <View style={tw`w-full`}>
          {isMobileUX &&
            (!team.isShared || (!!team.role && team.role !== "moderator")) && (
              <NewTestButton style={tw`h-[62px] mx-5 my-2.5`} />
            )}
        </View>
      </Container>
    </>
  );
};

export default EventList;
